import { useState } from 'react';
import { useWorkspaces } from '../../api/WorkspacesContext';
import type { WorkspaceSummary } from '../../api/workspaces';
import { CURRENT_USER } from '../../map/MapContext';
import { useNavigation } from '../../navigation/NavigationContext';
import { ContentIcon, SearchIcon, WorkspacesIcon } from '../../components/icons/Icons';
import { LogoMark } from '../../components/icons/LogoMark';
import { Tooltip } from '../../components/Tooltip/Tooltip';
import { ContentExplorer } from './ContentExplorer';
import { styleForWorkspace } from './workspaceStyle';
import './home-page.css';

function WorkspaceCard({ workspace, onOpen }: { workspace: WorkspaceSummary; onOpen: () => void }) {
  const { Icon, accent, accent2 } = styleForWorkspace(workspace.icon);
  return (
    <button type="button" className="workspace-card" onClick={onOpen}>
      <div
        className="workspace-card-thumb"
        style={{ background: `radial-gradient(120% 120% at 20% 15%, ${accent2} 0%, var(--surface-2) 70%)` }}
      >
        <span className="workspace-card-thumb-icon" style={{ color: accent }}>
          <Icon size={34} />
        </span>
        {workspace.empty && <span className="workspace-card-empty-badge">No data yet</span>}
      </div>
      <div className="workspace-card-body">
        <div className="workspace-card-title-row">
          <span className="workspace-card-title">{workspace.title}</span>
          <span className="workspace-card-avatars">
            <span className="workspace-avatar">{CURRENT_USER}</span>
            <span className="workspace-avatar workspace-avatar-overflow">+2</span>
          </span>
        </div>
        <p className="workspace-card-description">{workspace.description}</p>
        <div className="workspace-card-meta">{workspace.empty ? 'No data yet' : 'Live'}</div>
      </div>
    </button>
  );
}

function WorkspaceGrid({ query }: { query: string }) {
  const state = useWorkspaces();
  const { goToMap } = useNavigation();

  if (state.status === 'loading') {
    return <p className="home-loading" role="status">Loading workspaces…</p>;
  }

  if (state.status === 'error') {
    return (
      <div className="home-error" role="alert">
        <p>Couldn&rsquo;t load workspaces. {state.message}</p>
        <button type="button" className="app-retry-btn" onClick={state.retry}>
          Retry
        </button>
      </div>
    );
  }

  const visible = state.workspaces.filter((w) => w.title.toLowerCase().includes(query.toLowerCase()));

  return (
    <div className="workspace-grid">
      {visible.map((w) => (
        <WorkspaceCard key={w.workspace_id} workspace={w} onOpen={() => goToMap(w.slug)} />
      ))}
      {visible.length === 0 && (
        <p className="home-no-results">No workspaces match &ldquo;{query}&rdquo;.</p>
      )}
    </div>
  );
}

/**
 * Home/landing page — reached by clicking the Abrar Farms logo in the top
 * bar (see TopBar.tsx), and the default view on login. Layout modeled on a
 * reference UI/UX pattern the user shared (layout inspiration only, own
 * branding). Each "workspace" card is a DATA VIEW within this single
 * farm's map (Imagery / Field Photos / Survey / Boundaries placeholder),
 * not a separate farm site — clicking one opens the map viewer landed in a
 * focused state for that group (NavigationContext.goToMap + SceneManager's
 * FOCUS_MAP). Workspace data itself comes from GET /api/v1/workspaces
 * (ROADMAP.md v0.6.2) via WorkspacesContext, not a frontend constant.
 */
export function HomePage() {
  // Which section is showing comes from the URL ('/' vs '/content', see
  // navigation/routes.ts), not local state — so a refresh or a direct link
  // to /content keeps the Content tab active.
  const { homeNav: nav, setHomeNav: setNav } = useNavigation();
  const [query, setQuery] = useState('');

  return (
    <div className="home-page">
      <aside className="home-nav">
        <div className="home-nav-brand">
          <LogoMark size={32} className="home-nav-logo" />
          <span className="home-nav-brand-name">Abrar Farms</span>
        </div>

        <nav className="home-nav-items" aria-label="Home sections">
          <button
            type="button"
            className={`home-nav-item${nav === 'workspaces' ? ' active' : ''}`}
            onClick={() => setNav('workspaces')}
          >
            <WorkspacesIcon /> Workspaces
          </button>
          <button
            type="button"
            className={`home-nav-item${nav === 'content' ? ' active' : ''}`}
            onClick={() => setNav('content')}
          >
            <ContentIcon /> Content
          </button>
        </nav>

        <div className="home-nav-user">
          <span className="workspace-avatar">{CURRENT_USER}</span>
          <span className="home-nav-user-name">Hussain Abrar</span>
        </div>
      </aside>

      <main className="home-main">
        {nav === 'workspaces' && (
          <>
            <div className="home-main-header">
              <h1>My workspaces</h1>
              <div className="home-main-controls">
                <div className="home-search">
                  <SearchIcon />
                  <input
                    type="text"
                    placeholder="Search workspaces"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    aria-label="Search workspaces"
                  />
                </div>
                <Tooltip heading="Sort" description="Not available yet.">
                  <button type="button" className="home-sort-btn">
                    Last modified
                  </button>
                </Tooltip>
              </div>
            </div>

            <WorkspaceGrid query={query} />
          </>
        )}

        {nav === 'content' && <ContentExplorer />}
      </main>
    </div>
  );
}
